import React from 'react';

import { RedocStandalone } from 'redoc';
import { Title } from 'react-admin';
import semverGte from 'semver/functions/gte';
import tradeinApiSpec from '@troverecommerce/ps-tradein-api-core/build/ps-tradein-api.json';
import repairApiSpec from '@troverecommerce/ps-repair-api-core/build/ps-repair-api.json';
import { getEnv } from '@src/apis/shared';
import { OpenAPIV3_1 } from 'openapi-types';
import { useTitle } from '@src/hooks/useTitle';


const YERDLE_ENV = getEnv('YERDLE_ENV');

const getPaths = (specs: OpenAPIV3_1.PathsObject<{}, {}> | undefined, prefix: string) => {
  if (!specs) return {};
  return Object.keys(specs).reduce((memo, path) => {
    return {
      ...memo,
      [`/${prefix}${path}`]: specs[path],
    };
  }, {});
};

const getServerUrl = () => {
  if (['development', 'qa', 'staging', 'sandbox'].includes(YERDLE_ENV)) {
    return `https://api.${YERDLE_ENV}.trove.com/v2`;
  }
  return 'https://api.trove.com/v2';
};

const servers = [
  {
    url: getServerUrl(),
    description: 'Production Partner Suite API',
  }
];

const info = {
  title: 'Trove Partner Suite API',
  description: 'This is the Partner Suite API.',
  termsOfService: 'https://trove.com/terms-of-service/',
  version: '2.0.0',
  contact: {
    name: 'Trove Engineering',
    email: 'dev@trove.co'
  }
};

const getSpec = (spec: OpenAPIV3_1.Document): OpenAPIV3_1.Document => {
  if (YERDLE_ENV === 'production' && !semverGte(spec.info.version, '1.0.0')) {
    return {
      openapi: '3.1.1',
      info: {
        summary: 'Not available',
        title: 'Not available',
        version: spec.info.version,
      },
      paths: undefined,
      components: {},
    };
  }
  return spec;
};

const tradeinSpec = getSpec(tradeinApiSpec as OpenAPIV3_1.Document);
const repairSpec = getSpec(repairApiSpec as OpenAPIV3_1.Document);

const mergedSpec = {
  openapi: '3.1.1',
  servers,
  info,
  paths: {
    ...getPaths(tradeinSpec.paths, 'tradein'),
    ...getPaths(repairSpec.paths, 'repair'),
  },
  components: {
    ...tradeinSpec.components,
    ...repairSpec.components,
    schemas: {
      ...tradeinSpec.components?.schemas,
      ...repairSpec.components?.schemas,
    },
    responses: {
      ...tradeinSpec.components?.responses,
      ...repairSpec.components?.responses,
    },
    securitySchemes: {
      ...tradeinSpec.components?.securitySchemes,
      ...repairSpec.components?.securitySchemes,
    },
  },
  tags: [
    ...(tradeinSpec.tags || []),
    ...(repairSpec.tags || []),
  ],
};


const ApiDocView = () => {
  const { title } = useTitle('API Documentation');

  return (
    <>
      <Title title={title} />
      <RedocStandalone
        spec={mergedSpec}
      />
    </>
  );
};

export default ApiDocView;
